import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/** Texto visible traducible: lo trae window.tmI18n (layout/theme.liquid); si falta, vale el español. */
function tr(key, fallback, vars) {
  const t = (window.tmI18n && window.tmI18n[key]) || fallback;
  return vars ? t.replace(/\{\{(\w+)\}\}/g, (_, n) => (vars[n] !== undefined ? vars[n] : '')) : t;
}

/** Visitante en ingles: textos y decimales en ingles (punto), y la muestra se guarda en ingles. */
const enIngles = () => (document.documentElement.lang || '').toLowerCase().startsWith('en');

const INSTALLATION_LABELS = { mate: 'Con pegamento a la pared', satinado: 'Pre-encolado', lino: 'Autoadhesivo' };

const CM_PER_INCH = 2.54;

/** Input constraints and ruler tick spacing per length unit the shopper can enter Ancho/Alto in. */
const LENGTH_UNITS = {
  cm: { label: 'cm', min: 100, max: 700, step: 10, majorEvery: 50, minorEvery: 10 },
  in: { label: 'in', min: 40, max: 276, step: 1, majorEvery: 12, minorEvery: 2 },
};

function toCm(value, unit) {
  return unit === 'in' ? value * CM_PER_INCH : value;
}

function fromCm(valueCm, unit) {
  return unit === 'in' ? valueCm / CM_PER_INCH : valueCm;
}

/** 1 m² in each alternate area unit, and the label to show for it. */
const AREA_UNITS = {
  m2: { factor: 1, label: 'm²' },
  ft2: { factor: 10.7639, label: 'ft²' },
  in2: { factor: 1550.0031, label: 'in²' },
};

/**
 * Shared pricing math for both the modal and inline calculators: the rate
 * for a given installation type (base + its addon) and the total price for
 * a width/height/rate combination (always in cm, converted from whichever
 * unit the shopper is entering measurements in). Kept in one place since
 * this is the part most likely to silently drift if hand-duplicated
 * between the two custom elements.
 */
function addonFor(finish, satinAddon, linenAddon) {
  if (finish === 'satinado') return satinAddon;
  if (finish === 'lino') return linenAddon;
  return 0;
}

/**
 * Cuántas unidades de la variante hay que pedir para que el carrito cobre el
 * total calculado.
 *
 * Shopify solo acepta cantidades enteras y cobra precio de variante por
 * unidad, así que la única forma de cobrar una superficie sin Shopify Plus es
 * que la variante valga la tarifa de una fracción de metro y pedir tantas
 * fracciones como haga falta.
 *
 * Se deduce de lo que valga la variante en cada momento, no de una constante:
 * mientras la variante siga costando la tarifa de un metro entero, esto
 * devuelve 1 y el carrito se comporta como hasta ahora. Cuando baje a la
 * tarifa de 0,01 m², devuelve los centenares que tocan y el total cuadra al
 * céntimo. Sin ventana en la que el carrito cobre cien veces de más o de
 * menos.
 *
 * @param {number} totalMinorUnits - total calculado, en céntimos
 * @param {number} variantMinorUnits - precio de la variante, en céntimos
 * @returns {number} cantidad entera a pedir
 */
function quantityFor(totalMinorUnits, variantMinorUnits) {
  if (!variantMinorUnits || variantMinorUnits <= 0) return 1;
  // Un precio de variante por encima de 1 EUR solo puede ser la tarifa de un
  // metro cuadrado entero: ahí el truco no aplica todavía.
  if (variantMinorUnits > 100) return 1;
  return Math.max(1, Math.round(totalMinorUnits / variantMinorUnits));
}

function computeTotal(widthCm, heightCm, ratePerSquareMeter) {
  const squareMeters = Math.max(1, (widthCm * heightCm) / 10000);
  const total = squareMeters * ratePerSquareMeter;
  return { squareMeters, minorUnits: Math.round(total * 100) };
}

/**
 * Formats a computed area (always tracked internally in m², since that's
 * the real pricing unit) in whichever display unit the shopper picked —
 * m², sq ft or sq in — purely for their convenience. The price itself is
 * never affected by this choice.
 */
function formatArea(squareMeters, unit) {
  const { factor, label } = AREA_UNITS[unit] ?? AREA_UNITS.m2;
  const n = (squareMeters * factor).toFixed(2);
  return `${enIngles() ? n : n.replace('.', ',')} ${label}`;
}

/** Decimales con coma, que es como se escriben en español. */
function coma(n) {
  return String(n).replace('.', ',');
}

/**
 * Adds a line to the real Shopify cart and notifies the theme's cart
 * drawer/icon via the Standard Events API, exactly like `product-form.js`
 * does. Shared by the modal calculator, the inline calculator and the
 * sample-request button so this integration (the trickiest part to get
 * right — see tailor-murals-theme-status memory) only exists once.
 *
 * @param {HTMLElement} element - dispatches the cart event on this element
 * @param {{variantId: string, productId?: string, properties: Record<string, string>, sourceId: string, quantity?: number}} data
 */
async function addLineToCart(element, { variantId, productId, properties, sourceId, quantity = 1 }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  element.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity, properties }],
        }),
      })
    );

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.message || result.description || tr('cart_error', 'No se pudo añadir al carrito'));
    }

    const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'mural-calculator',
        sourceId,
        itemCount: 1,
        productId,
      },
    });
  } catch (error) {
    deferred.resolve({ cart: null, detail: { didError: true } });
    throw error;
  }
}

/**
 * On-page mural calculator: material, dimensions (cm or inches) and add to
 * cart, always visible on the product page — no dialog, no separate trigger.
 * The scaled wall photo with its ruler and draggable crop lives in the
 * separate mural-wall-preview component alongside this; it publishes
 * width/height/crop here via the 'mural:dimensions' event (see
 * onWallDimensions below) rather than this class owning that UI itself.
 *
 * There used to be a second calculator — a modal opened from collection
 * card images — duplicating this one's pricing/cart logic with a plainer,
 * ruler-only preview and no wall context. Removed: clicking a card image
 * already lands here, so the modal was one path to the same result plus a
 * worse version of it. Its module-level pricing/ruler/pan helpers went with
 * it where nothing else called them; addLineToCart, computeTotal and the
 * rest below stayed since this class still uses them.
 *
 * @typedef {object} MuralCalculatorInlineRefs
 * @property {HTMLElement} imageCrop
 * @property {HTMLImageElement} image
 * @property {HTMLElement} rulerH
 * @property {HTMLElement} rulerV
 * @property {HTMLInputElement} widthInput
 * @property {HTMLInputElement} heightInput
 * @property {HTMLElement} widthUnitLabel
 * @property {HTMLElement} heightUnitLabel
 * @property {HTMLElement} m2Value
 * @property {HTMLElement} priceValue
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} formEl
 * @property {HTMLElement} successEl
 * @property {HTMLElement} errorEl
 * @property {HTMLElement[]} [finishButtons]
 * @property {HTMLElement[]} [finishPrice]
 * @property {HTMLElement[]} [unitButtons]
 * @property {HTMLElement[]} [lengthUnitButtons]
 *
 * @extends {Component<MuralCalculatorInlineRefs>}
 */
export class MuralCalculatorInline extends Component {
  // imageCrop, image, rulerH y rulerV no entran aqui: son la vista previa a
  // escala, que la ficha de producto oculta y solo muestra la plantilla
  // ?view=calculator. Si se declaran obligatorios, Component aborta el
  // arranque y la calculadora deja de dar metros y precio.
  requiredRefs = [
    'widthInput',
    'heightInput',
    'priceValue',
    'addButton',
    'formEl',
    'successEl',
    'errorEl',
  ];

  connectedCallback() {
    super.connectedCallback();
    this.selectedFinish = 'mate';
    this.selectedUnit = 'm2';
    this.selectedLengthUnit = 'cm';
    this.variantId = this.dataset.variantId;
    this.productId = this.dataset.productId;
    // La vista previa a escala es opcional: la ficha de producto la oculta y
    // solo la ensena la plantilla ?view=calculator.
    if (this.refs.image) this.refs.image.style.objectPosition = '50% 50%';

    // En la ficha de producto las medidas se piden en la vista de pared, junto
    // a la imagen grande. Los inputs de aqui siguen existiendo y siguen siendo
    // la fuente del calculo: la vista de pared solo les escribe el valor.
    this.onWallDimensions = (/** @type {Event} */ event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      if (!detail) return;
      if (detail.width) this.refs.widthInput.value = String(detail.width);
      if (detail.height) this.refs.heightInput.value = String(detail.height);
      if (detail.focus) this.wallFocus = detail.focus;
      if (detail.crop) this.wallCrop = detail.crop;
      // Booleano, no truthy-string: hay que guardarlo siempre, tambien
      // cuando vuelve a false tras destoggle-arlo desde la vista de pared, si
      // no se quedaria pegado en "Si" para siempre en cuanto se activase una
      // vez.
      this.wallMirrored = detail.mirrored === true;
      this.sync();
    };
    document.addEventListener('mural:dimensions', this.onWallDimensions);

    // Página "Imprime tu mural": el bloque de subida publica si ya hay una
    // imagen subida y con derechos confirmados. Sin ella no se deja añadir al
    // carrito, y con ella su enlace viaja en las propiedades de la línea.
    this.requiresUpload = this.dataset.requiresUpload === 'true';
    this.onDesign = (/** @type {Event} */ event) => {
      this.design = /** @type {CustomEvent} */ (event).detail ?? null;
      if (this.design && this.design.ready) this.refs.errorEl.hidden = true;
    };
    document.addEventListener('mural:design', this.onDesign);

    this.sync();
  }

  disconnectedCallback() {
    if (this.onWallDimensions) {
      document.removeEventListener('mural:dimensions', this.onWallDimensions);
    }
    if (this.onDesign) document.removeEventListener('mural:design', this.onDesign);
    super.disconnectedCallback?.();
  }

  sync() {
    const { widthInput, heightInput, m2Value, priceValue, image } = this.refs;

    const lengthUnit = this.selectedLengthUnit;
    const widthRaw = Number(widthInput.value);
    const heightRaw = Number(heightInput.value);
    const widthCm = toCm(widthRaw, lengthUnit);
    const heightCm = toCm(heightRaw, lengthUnit);

    if (image) image.style.aspectRatio = `${widthCm} / ${heightCm}`;

    const priceBase = Number(this.dataset.priceBase ?? 0);
    const satinAddon = Number(this.dataset.finishSatinAddon ?? 0);
    const linenAddon = Number(this.dataset.finishLinenAddon ?? 0);
    const currency = this.dataset.currency;
    const moneyFormat = this.dataset.moneyFormat;

    // Visitantes de EE. UU.: el precio se da por pie cuadrado (el total no cambia).
    const usaPies = this.dataset.areaUnit === 'ft2';
    const porPie = (r) => formatMoney(Math.round((r / AREA_UNITS.ft2.factor) * 100), moneyFormat, currency);
    const finishButtons = this.refs.finishButtons ?? [];
    const finishPrices = this.refs.finishPrice ?? [];
    finishButtons.forEach((button, i) => {
      const rate = priceBase + addonFor(button.dataset.finish, satinAddon, linenAddon);
      const priceEl = finishPrices[i];
      const porM2 = formatMoney(Math.round(rate * 100), moneyFormat, currency);
      if (priceEl) priceEl.textContent = usaPies ? `${porPie(rate)}/ft² (${porM2}/m²)` : `${porM2}/m²`;
    });

    const rate = priceBase + addonFor(this.selectedFinish, satinAddon, linenAddon);
    const { squareMeters, minorUnits } = computeTotal(widthCm, heightCm, rate);

    // El contador de metros es opcional: la ficha de producto lo oculta y
    // deja solo el total. Sin esta guarda, el TypeError cortaba sync()
    // justo antes de escribir el precio y el total salia vacio.
    if (m2Value) m2Value.textContent = formatArea(squareMeters, this.selectedUnit);

    // La superficie exacta, presentada como la cuenta que se esta haciendo.
    // Un numero suelto al lado del total no decia de donde salia.
    if (this.refs.sumValue) {
      // Coma decimal solo en espanol; en ingles, punto.
      const dec = (n) => (enIngles() ? n : n.replace('.', ','));
      const metros = dec(squareMeters.toFixed(2));
      const pies = (squareMeters * AREA_UNITS.ft2.factor).toFixed(2);
      const porM2 = formatMoney(Math.round(rate * 100), moneyFormat, currency);
      this.refs.sumValue.textContent = usaPies
        ? `${pies} ft² × ${porPie(rate)}/ft² (${metros} m²)`
        : `${metros} m² (${dec(pies)} ft²) × ${porM2}/m²`;
    }

    priceValue.textContent = formatMoney(minorUnits, moneyFormat, currency);
  }

  /** @param {{finish: string}} data */
  selectFinish({ finish }) {
    this.selectedFinish = finish;
    // Se recuerda para que el boton "Pedir muestra" de la ficha (que no tiene
    // calculadora) lleve el material que el cliente ya habia elegido.
    try { localStorage.setItem('tmFinish', finish); } catch (e) { /* sin almacenamiento: se usa el de por defecto */ }

    for (const button of this.refs.finishButtons ?? []) {
      button.classList.toggle('is-selected', button.dataset.finish === finish);
    }

    this.sync();
  }

  /** @param {{unit: string}} data */
  selectUnit({ unit }) {
    this.selectedUnit = unit;

    for (const button of this.refs.unitButtons ?? []) {
      button.classList.toggle('is-selected', button.dataset.unit === unit);
    }

    this.sync();
  }

  /** Switches which unit Ancho/Alto are entered in (cm or in), converting the current values. */
  selectLengthUnit({ unit }) {
    if (unit === this.selectedLengthUnit) return;

    const { widthInput, heightInput } = this.refs;
    const widthCm = toCm(Number(widthInput.value), this.selectedLengthUnit);
    const heightCm = toCm(Number(heightInput.value), this.selectedLengthUnit);

    this.selectedLengthUnit = unit;
    const config = LENGTH_UNITS[unit] ?? LENGTH_UNITS.cm;

    widthInput.value = String(Math.round(fromCm(widthCm, unit)));
    heightInput.value = String(Math.round(fromCm(heightCm, unit)));
    for (const input of [widthInput, heightInput]) {
      input.min = String(config.min);
      input.max = String(config.max);
      input.step = String(config.step);
    }
    if (this.refs.widthUnitLabel) this.refs.widthUnitLabel.textContent = config.label;
    if (this.refs.heightUnitLabel) this.refs.heightUnitLabel.textContent = config.label;

    for (const button of this.refs.lengthUnitButtons ?? []) {
      button.classList.toggle('is-selected', button.dataset.lengthUnit === unit);
    }

    this.sync();
  }

  async addToCart() {
    if (!this.variantId) return;

    if (this.requiresUpload && !(this.design && this.design.ready)) {
      this.refs.errorEl.textContent =
        (this.design && this.design.reason) || tr('upload_needed', 'Sube la imagen de tu mural para continuar.');
      this.refs.errorEl.hidden = false;
      return;
    }

    // La vista de pared publica sus medidas al conectarse. Si esta calculadora
    // se registro despues, ese primer aviso se perdio y ni el encuadre ni la
    // zona impresa llegarian al pedido: le pasaba a todo el que aceptaba las
    // medidas por defecto, que es el camino mas frecuente. Se le pide el estado
    // ahora, que es el unico momento en que hace falta. dispatchEvent es
    // sincrono, asi que al volver de sync() los datos ya estan puestos.
    if (!this.wallCrop) {
      const pared = document.querySelector('mural-wall-preview');
      if (pared && typeof pared.sync === 'function') pared.sync();
    }

    const widthCm = toCm(Number(this.refs.widthInput.value), this.selectedLengthUnit);
    const heightCm = toCm(Number(this.refs.heightInput.value), this.selectedLengthUnit);
    const finishLabel = INSTALLATION_LABELS[this.selectedFinish] ?? INSTALLATION_LABELS.mate;

    this.refs.addButton.disabled = true;
    this.refs.addButton.textContent = tr('adding', 'Añadiendo…');
    this.refs.errorEl.hidden = true;

    try {
      const rate =
        Number(this.dataset.priceBase ?? 0) +
        addonFor(
          this.selectedFinish,
          Number(this.dataset.finishSatinAddon ?? 0),
          Number(this.dataset.finishLinenAddon ?? 0)
        );
      const { squareMeters, minorUnits } = computeTotal(widthCm, heightCm, rate);

      await addLineToCart(this, {
        variantId: this.variantId,
        productId: this.productId,
        quantity: quantityFor(minorUnits, Number(this.dataset.variantPrice ?? 0)),
        properties: {
          Alto: `${Math.round(heightCm)} cm`,
          Ancho: `${Math.round(widthCm)} cm`,
          Superficie: `${squareMeters.toFixed(2).replace('.', ',')} m²`,
          Instalación: finishLabel,
          // Solo en "Imprime tu mural": lo que necesita producción para
          // imprimir el archivo del cliente y comprobar el pedido.
          ...(this.requiresUpload && this.design && this.design.image
            ? {
                Imagen: this.design.image.url,
                Archivo: this.design.image.name,
                'Tamaño de la imagen': `${this.design.image.width} × ${this.design.image.height} px`,
                ...(this.design.ppi ? { 'Resolución estimada': `${this.design.ppi} ppp` } : {}),
                'Derechos de la imagen': 'Confirmados por el cliente',
              }
            : {}),
          // Lo que necesita producción: qué rectángulo del diseño original
          // hay que imprimir. El encuadre en bruto se guarda también, porque
          // es lo que permite reconstruir la vista previa tal como la vio el
          // cliente si alguna vez hay una reclamación.
          ...(this.wallCrop
            ? {
                'Zona impresa': `horizontal ${coma(this.wallCrop.x1)} % a ${coma(this.wallCrop.x2)} % · vertical ${coma(this.wallCrop.y1)} % a ${coma(this.wallCrop.y2)} % (se usa el ${this.wallCrop.usado} % del diseño)`,
              }
            : {}),
          ...(this.wallFocus ? { Encuadre: this.wallFocus } : {}),
          // Volteo horizontal pedido desde el boton "Espejo" de la vista de
          // pared. Solo se manda cuando esta activo: un pedido sin esta
          // propiedad significa "sin voltear", igual que pasaba antes de que
          // existiera el boton -- no hace falta un "Espejo: No" explicito.
          ...(this.wallMirrored ? { Espejo: 'Sí' } : {}),
        },
        sourceId: this.id,
      });

      this.refs.formEl.hidden = true;
      this.refs.successEl.hidden = false;

      setTimeout(() => {
        this.refs.formEl.hidden = false;
        this.refs.successEl.hidden = true;
        this.refs.addButton.disabled = false;
        this.refs.addButton.textContent = tr('add_to_cart', 'Añadir al carrito');
      }, 2200);
    } catch (error) {
      this.refs.errorEl.textContent = error instanceof Error ? error.message : tr('cart_error', 'No se pudo añadir al carrito');
      this.refs.errorEl.hidden = false;
      this.refs.addButton.disabled = false;
      this.refs.addButton.textContent = tr('add_to_cart', 'Añadir al carrito');
    }
  }
}

if (!customElements.get('mural-calculator-inline')) {
  customElements.define('mural-calculator-inline', MuralCalculatorInline);
}

const MUESTRAS_POR_PACK = 1;

const nombreMuestra = (n) => (enIngles() ? `Sample ${n}` : `Muestra ${n}`);
const MATERIALES_EN = { mate: 'Paper with glue on the wall', satinado: 'Pre-pasted', lino: 'Self-adhesive' };

/** Las muestras elegidas: diseño y material de cada una, en orden. */
function muestrasElegidas(panel) {
  return [...panel.querySelectorAll('.mural-sample__elegido')].map((li) => ({
    diseno: li.dataset.diseno ?? '',
    material: enIngles()
      ? (li.querySelector('.mural-sample__material')?.selectedOptions[0]?.textContent ?? '').trim()
      : li.querySelector('.mural-sample__material')?.value ?? '',
  }));
}

/** Cuántas veces está cada diseño en el pack. */
function vecesPorDiseno(panel) {
  const cuenta = new Map();
  for (const m of muestrasElegidas(panel)) cuenta.set(m.diseno, (cuenta.get(m.diseno) ?? 0) + 1);
  return cuenta;
}

/** Añade una muestra al pack, si queda hueco. */
function anadirMuestra(panel, diseno) {
  const lista = panel.querySelector('[data-sample-chosen]');
  const plantilla = panel.querySelector('[data-sample-row]');
  if (!lista || !plantilla) return;
  if (muestrasElegidas(panel).length >= MUESTRAS_POR_PACK) return;

  const fila = plantilla.content.firstElementChild.cloneNode(true);
  fila.dataset.diseno = diseno;
  fila.querySelector('.mural-sample__elegido-nombre').textContent = diseno;
  lista.appendChild(fila);
}

/** Marca en la rejilla lo que está en el pack, y cuántas veces. */
function sincronizarRejilla(panel) {
  const veces = vecesPorDiseno(panel);
  // Con una sola muestra no se apagan las demas: tocar otra sustituye a la elegida.
  const lleno = false;

  for (const opcion of panel.querySelectorAll('.mural-sample__opcion')) {
    const n = veces.get(opcion.dataset.diseno ?? '') ?? 0;
    opcion.classList.toggle('is-selected', n > 0);
    opcion.classList.toggle('is-bloqueada', lleno);
    opcion.setAttribute('aria-pressed', String(n > 0));
    // El "x2" solo aparece si de verdad va repetido; con uno seria ruido.
    const marca = opcion.querySelector('.mural-sample__veces');
    if (n > 1) {
      if (marca) marca.textContent = '×' + n;
      else {
        const s = document.createElement('span');
        s.className = 'mural-sample__veces';
        s.textContent = '×' + n;
        opcion.appendChild(s);
      }
    } else if (marca) marca.remove();
  }

  const lista = panel.querySelector('[data-sample-chosen]');
  if (lista) lista.hidden = muestrasElegidas(panel).length === 0;
}

/**
 * Pone al dia el contador y el boton de un panel de muestras.
 *
 * El pack son tres laminas por un precio fijo, asi que no se deja pedir con
 * menos: pagar por tres y recibir una es la clase de detalle que genera una
 * reclamacion. Al llegar a tres se apagan las demas opciones, que explica el
 * limite mejor que un mensaje de error despues de intentarlo.
 */
function refrescarPanelMuestras(panel) {
  const muestras = muestrasElegidas(panel);
  const completo = muestras.length === MUESTRAS_POR_PACK;

  const contador = panel.querySelector('[data-sample-count]');
  if (contador) {
    contador.textContent = completo
      ? tr('samples_complete', 'Muestra elegida. Cambia el material si quieres ver otro acabado.')
      : tr('samples_progress', '{{n}} de {{total}} elegidas', { n: muestras.length, total: MUESTRAS_POR_PACK });
  }

  sincronizarRejilla(panel);

  const boton = panel.querySelector('[data-sample-trigger]');
  if (boton && !boton.hasAttribute('disabled')) {
    boton.setAttribute('aria-disabled', String(!completo));
    boton.textContent = completo
      ? tr('samples_add_pack', 'Añadir la muestra')
      : tr('samples_pick_more', 'Elige un diseño', { n: MUESTRAS_POR_PACK - muestras.length });
  }
}

/** Sin tildes y en minusculas, para que "montanas" encuentre "Montañas". */
function sinTildes(texto) {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

document.addEventListener('input', (event) => {
  const campo = /** @type {HTMLInputElement | null} */ (event.target instanceof Element
    ? event.target.closest('[data-sample-filter]')
    : null);
  if (!campo) return;

  const panel = campo.closest('.mural-sample');
  if (!panel) return;

  const busca = sinTildes(campo.value);
  let coincidencias = 0;
  for (const opcion of panel.querySelectorAll('.mural-sample__opcion')) {
    // Lo ya elegido no se esconde nunca: si el filtro se lo llevara, el cliente
    // veria el contador en 3 sin ver por que.
    const elegida = opcion.classList.contains('is-selected');
    const coincide = !busca || sinTildes(opcion.dataset.diseno ?? '').includes(busca);
    opcion.hidden = !coincide && !elegida;
    if (coincide) coincidencias += 1;
  }

  // Se cuentan las coincidencias, no lo que queda a la vista: si la busqueda no
  // encuentra nada pero hay un elegido anclado, sin esto el cliente veria una
  // tarjeta suelta y ningun aviso que explicase por que.
  const vacio = panel.querySelector('[data-sample-empty]');
  if (vacio) vacio.hidden = coincidencias > 0;
});

document.addEventListener('click', (event) => {
  const quitar = /** @type {HTMLElement | null} */ (event.target instanceof Element
    ? event.target.closest('[data-sample-remove]')
    : null);
  if (quitar) {
    event.preventDefault();
    const fila = quitar.closest('.mural-sample__elegido');
    const panel = quitar.closest('.mural-sample');
    if (fila && panel) {
      fila.remove();
      refrescarPanelMuestras(panel);
    }
    return;
  }

  const opcion = /** @type {HTMLElement | null} */ (event.target instanceof Element
    ? event.target.closest('.mural-sample__opcion')
    : null);
  if (!opcion) return;

  event.preventDefault();
  const panel = opcion.closest('.mural-sample');
  if (!panel) return;

  // Con una sola muestra, tocar otro diseno sustituye al elegido (el material
  // que ya se habia puesto se conserva).
  const actual = panel.querySelector('.mural-sample__elegido');
  const materialPrevio = actual?.querySelector('.mural-sample__material')?.value;
  if (actual) actual.remove();
  anadirMuestra(panel, opcion.dataset.diseno ?? '');
  const nueva = panel.querySelector('.mural-sample__elegido .mural-sample__material');
  if (nueva && materialPrevio) nueva.value = materialPrevio;
  refrescarPanelMuestras(panel);
});

// El diseño de la ficha viene marcado desde Liquid, asi que el contador tiene
// que reflejarlo ya en la primera pintada.
for (const panel of document.querySelectorAll('.mural-sample')) refrescarPanelMuestras(panel);

/**
 * "Pedir una muestra" button on a mural's product page: adds the store's one
 * fixed sample product (looked up by its handle, `all_products['muestra-a4']`
 * in Liquid) to the cart with a `Diseño` line-item property recording which
 * mural the shopper wants a physical swatch of — same "visual property,
 * human confirms details" pattern already used for Ancho/Alto/Instalación.
 */
document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement | null} */ (event.target instanceof Element
    ? event.target.closest('[data-sample-trigger]')
    : null);

  if (!trigger) return;

  event.preventDefault();

  const variantId = trigger.dataset.sampleVariantId;
  const productId = trigger.dataset.sampleProductId;
  if (!variantId) return;

  // "Pedir muestra" en la ficha de un mural: sin selector. La muestra es de ESTE
  // diseno y del material elegido en la calculadora (o el ultimo que se eligio),
  // y al anadirla se va directo al carrito.
  if (trigger.hasAttribute('data-sample-direct')) {
    const calc = /** @type {any} */ (trigger.closest('mural-calculator-inline'));
    let finish = calc?.selectedFinish;
    if (!finish) {
      try { finish = localStorage.getItem('tmFinish'); } catch (e) { finish = null; }
    }
    const material = (enIngles() ? MATERIALES_EN : INSTALLATION_LABELS)[finish] ?? (enIngles() ? MATERIALES_EN : INSTALLATION_LABELS).mate;
    const etiqueta = trigger.textContent;
    trigger.setAttribute('disabled', 'true');
    trigger.textContent = tr('adding', 'Añadiendo…');
    addLineToCart(trigger, {
      variantId,
      productId,
      // _Imagen y _Enlace empiezan por guion bajo: el carrito no los muestra, solo los usa
      // para pintar la foto y el enlace del mural en la linea de la muestra.
      properties: {
        [nombreMuestra(1)]: `${trigger.dataset.sampleDesignTitle ?? ''} · ${material}`,
        _Imagen: trigger.dataset.sampleImage ?? '',
        _Enlace: trigger.dataset.sampleUrl ?? '',
      },
      sourceId: 'mural-sample',
    })
      .then(() => { window.location.href = Theme.routes.cart_url; })
      .catch(() => {
        trigger.removeAttribute('disabled');
        trigger.textContent = etiqueta;
      });
    return;
  }

  const panel = trigger.closest('.mural-sample');
  const muestras = panel ? muestrasElegidas(panel) : [];

  // Se manda UNA unidad con las tres muestras como propiedades: el pack cuesta
  // lo mismo elija lo que elija.
  if (muestras.length !== MUESTRAS_POR_PACK) {
    if (panel) {
      const contador = panel.querySelector('[data-sample-count]');
      if (contador) contador.textContent = tr('samples_missing', 'Elige el diseño de tu muestra.', { total: MUESTRAS_POR_PACK, n: MUESTRAS_POR_PACK - muestras.length });
    }
    return;
  }

  /** @type {Record<string, string>} */
  const sampleProperties = {};
  muestras.forEach((m, i) => {
    sampleProperties[nombreMuestra(i + 1)] = `${m.diseno} · ${m.material}`;
  });

  const originalLabel = trigger.textContent;
  trigger.setAttribute('disabled', 'true');
  trigger.textContent = tr('adding', 'Añadiendo…');

  addLineToCart(trigger, {
    variantId,
    productId,
    properties: sampleProperties,
    sourceId: 'mural-sample',
  })
    .then(() => {
      trigger.textContent = tr('samples_added', 'Muestra añadida');
      setTimeout(() => {
        trigger.removeAttribute('disabled');
        trigger.textContent = originalLabel;
        if (panel) refrescarPanelMuestras(panel);
      }, 2200);
    })
    .catch(() => {
      trigger.removeAttribute('disabled');
      trigger.textContent = originalLabel;
      if (panel) refrescarPanelMuestras(panel);
    });
});
